import { Fragment } from 'react';
import { Route, Routes } from 'react-router-dom';
import { t } from '@lingui/macro';

import {
    AdminActiveStandbyPlmList,
    AdminAgentDetailHealthPage,
    AdminAgentDetailPage,
    AdminAgentListPage,
    AdminAgentSettingsDetail,
    AdminApiTokensDetail,
    AdminApiTokensList,
    AdminAuditActivities,
    AdminBusinessServiceDetail,
    AdminBusinessServiceList,
    AdminCertificateDetailRoutePage,
    AdminCertificatesPage,
    AdminGeneralPage,
    AdminISNGList,
    AdminLicenseDetailPage,
    AdminPollingPage,
    AdminServerHealthPage,
    AdminSiteDetail,
    AdminSiteList,
    AdminSsoDetail,
    AdminSyslog,
    AdminUserDetail,
    AdminUserList,
    AdminWanInterfaceDetail,
    AdminWifiNetworksDetail,
    AdminWifiNetworksList,
    AdminWiredNetworksDetail,
    AdminWiredNetworksList,
    CollectorsAdminDetails,
    CollectorsAdminPage,
    DashboardsAdminDetails,
    DashboardsAdminList,
    GroupsAdminDetails,
    GroupsAdminList,
    MaintenanceWindowsDetail,
    MaintenanceWindowsList,
    MonitoredInfrastructureDetail,
    MonitoredInfrastructureList,
    MonitoredInterfaceDetail,
    MonitoredInterfaceList,
    NotificationPolicyDetail,
    TestsDetailPage,
    TestsListPage,
    VirtualInfrastructureDetail,
    VirtualInfrastructureList,
    VoipAdminPage,
    VoipAgentDetailPage,
    VoipAgentListPage,
    VoipCallManagersDetailPage,
    VoipCallManagersListPage,
} from '.';
import {
    buildAdminRoute,
    buildListDetailAdminRoute,
    buildRedirectAdminRoute,
    buildSysAdminRoute,
    buildTabbedAdminRoute,
    buildVoipAdminRoute,
    LastAdminPageRedirect,
    LastAdminPageRedirectProvider,
    RouteWithPersistence,
} from './AdminPagesHelper';
import { NavigationMenu, PlmAdminMenu } from '../side-nav';
import { AdminPages, DEFAULT_ID_PROPERTY } from './AdminPages';
import { addPlmPrefix, Navigate, usePlmId } from '../../utilities/PgmUtilities';
import { FilterStateContainer, INITIAL_STATE } from '../../stores/FilterStateStore';
import AuthRoleGuard from '../auth/AuthRoleGuard';
import { useIsPgm } from '../../stores/EnvironmentStore';
import {
    DEFAULT_TAB as DEFAULT_TAB_NOTIFICATIONS,
    VALID_TABS as VALID_TABS_NOTIFICATIONS,
} from './notification-policy/NotificationPolicyDetail';
import { DEFAULT_TAB as DEFAULT_TAB_POLLING, VALID_TABS as VALID_TABS_POLLING } from './polling/PollingAdminPage';
import ThresholdsAdminPage from './thresholds/ThresholdsAdminPage';
import TestTypesAdminPage from './testTypes/TestTypesAdminPage';
import TestTypesDetailPage from './testTypes/TestTypesDetailPage';
import TestIdPage from './tests/TestIdPage';
import TestErrorDetailPage from './testTypes/TestErrorDetailPage';

const emptyFilterState = {
    ...INITIAL_STATE,
    initialized: true,
};

// this comes from portal/ui land. Keep this consistent until we drop support for portal/ui.
const LAST_ADMIN_PAGE_KEY = 'last-admin-page-key';

export default function PlmAdminPages() {
    const lastUrl = addPlmPrefix('/admin/');
    const plmId = usePlmId();
    const menuTitle = plmId ? t`Local > Administration` : t`Administration`;
    const [isPgm] = useIsPgm();

    // only allow users if we're directly on a PLM view, but not a PGM->PLM drilldown
    const isUsersPageEnabled = !isPgm && !plmId;

    // Admin pages don't have filter state, but they need an empty filter state for
    // the data table query loader to work.  This provides an empty, initialized state
    // so that the data table query loader will load.
    return (
        <AuthRoleGuard allowedRoles={['admin', 'super']}>
            <FilterStateContainer name="plm-admin-filter-state" state={emptyFilterState}>
                <NavigationMenu title={menuTitle} lastUrl={lastUrl} id="plm-admin" render={() => <PlmAdminMenu />}>
                    <LastAdminPageRedirectProvider pageKey={LAST_ADMIN_PAGE_KEY}>
                        <Routes className="flex-container">
                            <Route path="/" element={<LastAdminPageRedirect defaultPage={AdminPages.Sites} />} />

                            {[
                                isUsersPageEnabled ? (
                                    buildListDetailAdminRoute(AdminPages.Users, AdminUserList, AdminUserDetail)
                                ) : (
                                    <Fragment key="0" />
                                ),
                                buildListDetailAdminRoute(
                                    AdminPages.WifiNetworks,
                                    AdminWifiNetworksList,
                                    AdminWifiNetworksDetail
                                ),
                                buildListDetailAdminRoute(
                                    AdminPages.WiredNetworks,
                                    AdminWiredNetworksList,
                                    AdminWiredNetworksDetail
                                ),
                                buildListDetailAdminRoute(
                                    AdminPages.ApiTokens,
                                    AdminApiTokensList,
                                    AdminApiTokensDetail
                                ),
                                buildAdminRoute(AdminPages.ISNGs, AdminISNGList),
                                buildAdminRoute(AdminPages.Sso, AdminSsoDetail),
                                buildSysAdminRoute(AdminPages.General, AdminGeneralPage),
                                buildListDetailAdminRoute(AdminPages.Sites, AdminSiteList, AdminSiteDetail),
                                buildAdminRoute(AdminPages.WanInterfaces, AdminWanInterfaceDetail),
                                buildListDetailAdminRoute(
                                    AdminPages.BusinessServices,
                                    AdminBusinessServiceList,
                                    AdminBusinessServiceDetail
                                ),
                                buildAdminRoute(AdminPages.AgentSettings, AdminAgentSettingsDetail),
                                buildListDetailAdminRoute(
                                    AdminPages.ServiceTests,
                                    TestTypesAdminPage,
                                    TestTypesDetailPage,
                                    {
                                        additionalRoutes: [
                                            <RouteWithPersistence
                                                path="errorDetails/:_id"
                                                element={<TestErrorDetailPage />}
                                            />,
                                            <RouteWithPersistence path="tests/:_id" element={<TestIdPage />} />,
                                            <Route path=":type/tests/*" key="tests">
                                                <RouteWithPersistence path="/" element={<TestsListPage />} />
                                                <RouteWithPersistence path=":_id" element={<TestsDetailPage />} />
                                            </Route>,
                                        ],
                                    }
                                ),
                                buildTabbedAdminRoute(AdminPages.Polling, AdminPollingPage, {
                                    defaultTab: DEFAULT_TAB_POLLING,
                                    validTabs: VALID_TABS_POLLING,
                                }),
                                buildRedirectAdminRoute(
                                    AdminPages.CallManagers,
                                    <Navigate replace to={`../${AdminPages.VoipConfig}/callManagers`} />,
                                    {
                                        key: 'legacy-callManagers-route',
                                    }
                                ),
                                buildAdminRoute(AdminPages.Thresholds, ThresholdsAdminPage),
                                buildVoipAdminRoute(
                                    AdminPages.VoipConfig,
                                    {
                                        indexComponent: VoipAdminPage,
                                        listComponent: VoipAgentListPage,
                                        detailComponent: VoipAgentDetailPage,
                                        callManagersListComponent: VoipCallManagersListPage,
                                        callManagersDetailComponent: VoipCallManagersDetailPage,
                                    },
                                    {
                                        idProperty: DEFAULT_ID_PROPERTY,
                                    }
                                ),
                                buildListDetailAdminRoute(AdminPages.Agents, AdminAgentListPage, AdminAgentDetailPage, {
                                    additionalRoutes: [
                                        <RouteWithPersistence
                                            path={`:${DEFAULT_ID_PROPERTY}/metrics`}
                                            element={<AdminAgentDetailHealthPage />}
                                        />,
                                    ],
                                }),
                                buildListDetailAdminRoute(
                                    AdminPages.AgentCertificates,
                                    AdminCertificatesPage,
                                    AdminCertificateDetailRoutePage
                                ),
                                buildAdminRoute(AdminPages.Licensing, AdminLicenseDetailPage),
                                buildAdminRoute(AdminPages.ServerHealth, AdminServerHealthPage),
                                buildAdminRoute(AdminPages.Syslog, AdminSyslog),
                                buildAdminRoute(AdminPages.AuditActivities, AdminAuditActivities),
                                buildAdminRoute(AdminPages.ActiveStandbyPlms, AdminActiveStandbyPlmList),
                                buildListDetailAdminRoute(
                                    AdminPages.MaintenanceWindows,
                                    MaintenanceWindowsList,
                                    MaintenanceWindowsDetail
                                ),
                                buildListDetailAdminRoute(AdminPages.Groups, GroupsAdminList, GroupsAdminDetails),
                                buildListDetailAdminRoute(
                                    AdminPages.Dashboards,
                                    DashboardsAdminList,
                                    DashboardsAdminDetails
                                ),
                                buildListDetailAdminRoute(
                                    AdminPages.Collectors,
                                    CollectorsAdminPage,
                                    CollectorsAdminDetails
                                ),
                                buildListDetailAdminRoute(
                                    AdminPages.VirtualInfrastructure,
                                    VirtualInfrastructureList,
                                    VirtualInfrastructureDetail
                                ),
                                buildTabbedAdminRoute(AdminPages.Notifications, NotificationPolicyDetail, {
                                    defaultTab: DEFAULT_TAB_NOTIFICATIONS,
                                    validTabs: VALID_TABS_NOTIFICATIONS,
                                }),
                                buildListDetailAdminRoute(
                                    AdminPages.MonitoredInfrastructure,
                                    MonitoredInfrastructureList,
                                    MonitoredInfrastructureDetail
                                ),
                                buildListDetailAdminRoute(
                                    AdminPages.Interfaces,
                                    MonitoredInterfaceList,
                                    MonitoredInterfaceDetail
                                ),
                            ]}
                        </Routes>
                    </LastAdminPageRedirectProvider>
                </NavigationMenu>
            </FilterStateContainer>
        </AuthRoleGuard>
    );
}
